import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { AuthProvider, useAuth } from './context/AuthContext';
import { ThemeProvider } from './context/ThemeContext';
import { useDevice } from './hooks';
import Layout from './components/Layout';

// Pages
import LandingPage from './pages/LandingPage';
import Login from './pages/Login';
import MobileLogin from './pages/MobileLogin';
import Register from './pages/Register';
import MobileRegister from './pages/MobileRegister';
import VerifyEmail from './pages/VerifyEmail';
import MobileVerifyEmail from './pages/MobileVerifyEmail';
import Dashboard from './pages/Dashboard';
import MobileDashboard from './pages/MobileDashboard';
import Search from './pages/Search';
import MobileSearch from './pages/MobileSearch';
import Movies from './pages/Movies';
import MobileMovies from './pages/MobileMovies';
import Series from './pages/Series';
import MobileSeries from './pages/MobileSeries';
import SeriesDetail from './pages/SeriesDetail';
import MobileSeriesDetail from './pages/MobileSeriesDetail';
import Profile from './pages/Profile';
import MobileProfileEdit from './pages/MobileProfileEdit';
import AdminUsers from './pages/AdminUsers';
import MobileAdminUsers from './pages/MobileAdminUsers';
import AdminSeries from './pages/AdminSeries';
import MobileAdminSeries from './pages/MobileAdminSeries';
import AdminSettings from './pages/AdminSettings';
import Top10 from './pages/Top10';
import MobileTop10 from './pages/MobileTop10';
import ResetPassword from './pages/ResetPassword';
import MobileResetPassword from './pages/MobileResetPassword';
import ForgotPassword from './pages/ForgotPassword';
import MobileForgotPassword from './pages/MobileForgotPassword';

/**
 * Componente que detecta dispositivo e renderiza dashboard apropriado
 */
function ResponsiveDashboard() {
  const { isMobile } = useDevice();
  return isMobile ? <MobileDashboard /> : <Dashboard />;
}

function ResponsiveSearch() {
  const { isMobile } = useDevice();
  return isMobile ? <MobileSearch /> : <Search />;
}

function ResponsiveLogin() {
  const { isMobile } = useDevice();
  return isMobile ? <MobileLogin /> : <Login />;
}

function ResponsiveRegister() {
  const { isMobile } = useDevice();
  return isMobile ? <MobileRegister /> : <Register />;
}

function ResponsiveVerifyEmail() {
  const { isMobile } = useDevice();
  return isMobile ? <MobileVerifyEmail /> : <VerifyEmail />;
}

function ResponsiveResetPassword() {
  const { isMobile } = useDevice();
  return isMobile ? <MobileResetPassword /> : <ResetPassword />;
}

function ResponsiveForgotPassword() {
  const { isMobile } = useDevice();
  return isMobile ? <MobileForgotPassword /> : <ForgotPassword />;
}

function ResponsiveMovies() {
  const { isMobile } = useDevice();
  return isMobile ? <MobileMovies /> : <Movies />;
}

function ResponsiveSeries() {
  const { isMobile } = useDevice();
  return isMobile ? <MobileSeries /> : <Series />;
}

function ResponsiveSeriesDetail() {
  const { isMobile } = useDevice();
  return isMobile ? <MobileSeriesDetail /> : <SeriesDetail />;
}

function ResponsiveAdminUsers() {
  const { isMobile } = useDevice();
  return isMobile ? <MobileAdminUsers /> : <AdminUsers />;
}

function ResponsiveAdminSeries() {
  const { isMobile } = useDevice();
  return isMobile ? <MobileAdminSeries /> : <AdminSeries />;
}

function ResponsiveTop10() {
  const { isMobile } = useDevice();
  return isMobile ? <MobileTop10 /> : <Top10 />;
}

function PrivateRoute({ children, adminOnly = false }) {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="loading-container">
        <div className="spinner"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" />;
  }

  if (adminOnly && !user.is_admin) {
    return <Navigate to="/dashboard" />;
  }

  return <Layout>{children}</Layout>;
}

function PublicRoute({ children }) {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="loading-container">
        <div className="spinner"></div>
      </div>
    );
  }

  if (user) {
    return <Navigate to="/dashboard" />;
  }

  return children;
}

function HomeRoute() {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="loading-container">
        <div className="spinner"></div>
      </div>
    );
  }

  if (user) {
    return <Navigate to="/dashboard" />;
  }

  return <LandingPage />;
}

function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <Router>
          <Toaster
            position="top-center"
            toastOptions={{
              duration: 3000,
              style: {
                background: 'var(--surface)',
                color: 'var(--text-primary)',
                borderRadius: '12px',
                padding: '16px 24px',
                fontSize: '15px',
                fontWeight: 500,
                boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)',
              },
              success: {
                iconTheme: {
                  primary: '#34C759',
                  secondary: 'white',
                },
              },
              error: {
                iconTheme: {
                  primary: '#FF3B30',
                  secondary: 'white',
                },
              },
            }}
          />
          <Routes>
            {/* Landing Page */}
            <Route path="/" element={<HomeRoute />} />

            {/* Public Routes */}
            <Route
              path="/login"
              element={
                <PublicRoute>
                  <ResponsiveLogin />
                </PublicRoute>
              }
            />
            <Route
              path="/register"
              element={
                <PublicRoute>
                  <ResponsiveRegister />
                </PublicRoute>
              }
            />
            <Route
              path="/verify-email"
              element={
                <PublicRoute>
                  <ResponsiveVerifyEmail />
                </PublicRoute>
              }
            />
            <Route path="/reset-password" element={<ResponsiveResetPassword />} />
            <Route
              path="/forgot-password"
              element={
                <PublicRoute>
                  <ResponsiveForgotPassword />
                </PublicRoute>
              }
            />

            {/* Private Routes - Responsive */}
            <Route
              path="/dashboard"
              element={
                <PrivateRoute>
                  <ResponsiveDashboard />
                </PrivateRoute>
              }
            />
            <Route
              path="/search"
              element={
                <PrivateRoute>
                  <ResponsiveSearch />
                </PrivateRoute>
              }
            />
            <Route
              path="/movies"
              element={
                <PrivateRoute>
                  <ResponsiveMovies />
                </PrivateRoute>
              }
            />
            <Route
              path="/series"
              element={
                <PrivateRoute>
                  <ResponsiveSeries />
                </PrivateRoute>
              }
            />
            <Route
              path="/series/:id"
              element={
                <PrivateRoute>
                  <ResponsiveSeriesDetail />
                </PrivateRoute>
              }
            />
            <Route
              path="/profile"
              element={
                <PrivateRoute>
                  <Profile />
                </PrivateRoute>
              }
            />
            <Route
              path="/profile/edit"
              element={
                <PrivateRoute>
                  <MobileProfileEdit />
                </PrivateRoute>
              }
            />

            {/* Top 10 Route */}
            <Route
              path="/top10"
              element={
                <PrivateRoute>
                  <ResponsiveTop10 />
                </PrivateRoute>
              }
            />

            {/* Admin Routes */}
            <Route
              path="/admin/users"
              element={
                <PrivateRoute adminOnly>
                  <ResponsiveAdminUsers />
                </PrivateRoute>
              }
            />
            <Route
              path="/admin/series"
              element={
                <PrivateRoute adminOnly>
                  <ResponsiveAdminSeries />
                </PrivateRoute>
              }
            />
            <Route
              path="/admin/settings"
              element={
                <PrivateRoute adminOnly>
                  <AdminSettings />
                </PrivateRoute>
              }
            />
          </Routes>
        </Router>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
